import type { Page } from '@/components/canvas/document/publicTypes';
import { pageChromeWorldHeight } from '@/components/canvas/workspace/model/pageArrangement';
import { getViewportWorldBounds } from '@/components/canvas/page/pageBounds';
import { getWorkspaceShellSize } from './elementInsertTarget';
import type { TextInsertVia } from './elementInsertTarget';

export interface DrawSurfaceRect {
  via: TextInsertVia;
  pageId?: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 页板内容区（data-page）在世界坐标中的矩形 */
export function getPageContentWorldRect(page: Page, zoom: number) {
  return {
    left: page.workspaceLayout.x,
    top: page.workspaceLayout.y,
    width: page.size.width,
    height: page.size.height,
  };
}

/** 世界坐标点是否落在某页内容区内（自上而下 zIndex 最高者优先） */
export function findPageAtWorldPoint(
  worldX: number,
  worldY: number,
  pages: Page[],
): Page | null {
  const sorted = [...pages].sort(
    (a, b) => (b.workspaceLayout.zIndex ?? 0) - (a.workspaceLayout.zIndex ?? 0),
  );
  for (const page of sorted) {
    const { left, top, width, height } = getPageContentWorldRect(page, 1);
    if (
      worldX >= left &&
      worldX < left + width &&
      worldY >= top &&
      worldY < top + height
    ) {
      return page;
    }
  }
  return null;
}

export function getWorkspaceViewportRect(
  panX: number,
  panY: number,
  zoom: number,
): DrawSurfaceRect {
  const { width, height } = getWorkspaceShellSize();
  const vb = getViewportWorldBounds(width, height, panX, panY, zoom);
  return {
    via: 'workspace',
    left: vb.x,
    top: vb.y,
    width: vb.width,
    height: vb.height,
  };
}

export function pageToDrawSurface(page: Page): DrawSurfaceRect {
  const { left, top, width, height } = getPageContentWorldRect(page, 1);
  return {
    via: 'page',
    pageId: page.id,
    left,
    top,
    width,
    height,
  };
}

/** 点击是否落在页标题栏（不应开始绘制） */
export function isPointOnPageChrome(
  worldX: number,
  worldY: number,
  page: Page,
  zoom: number,
): boolean {
  const chromeH = pageChromeWorldHeight(zoom);
  const { left, top } = getPageContentWorldRect(page, zoom);
  return (
    worldX >= left &&
    worldX < left + page.size.width &&
    worldY >= top - chromeH &&
    worldY < top
  );
}
